/**
 * Where a visitor came from, kept from their first visit until they sign up:
 * the founding code of a founding-gardener link (?founding=CODE), the
 * tagged link's utm_source, utm_medium and utm_campaign, and which of the
 * owner's own links brought them, her Instagram (?from=ig) or her Facebook
 * (?from=fb): the backend gives such an account the follower trial
 * (trial.FOLLOWER_DAYS, 30 days) and counts it apart from the ads. And two more from
 * the address: a share's credit (?ref=CODE, which a share page's "Start a
 * garden" carries, kept as the share page keeps it, so the credit survives a
 * link opened in another browser), and that the visit came to start a garden
 * (?start=1 from a garden page's or the website's "Start a garden", or the
 * ?ref), so the sign-in page opens on signing up (startsGarden). A public page's
 * "What is Grow?" and Grow's mark carry the credit with ?landing=1 (?ref=CODE&landing=1):
 * the credit is kept, but the visit goes to the landing page, not to sign-up.
 *
 * Kept in localStorage, as the share credit is (shares, REFERRAL_KEY),
 * because signing up is several screens after the link and the address has
 * long since changed: the link opens the sign-in page, then Google, then
 * back. CompleteSignup sends them with the new account, and the backend
 * keeps them on it (user.signup_source) and makes a founding gardener of an
 * open code. An account that already exists claims a code instead
 * (FoundingClaim). Quiet wherever storage is refused.
 *
 * Who uses it: main.tsx (keepArrival, before the app draws), LoginPage
 * (startsGarden), CompleteSignup (sends and forgets them),
 * features/billing FoundingClaim (a code claimed by an existing account) and
 * the landing page (keptFrom: a follower's hero and trial days on a later visit;
 * foundingThisVisit: the founding welcome only on a founding link's own visit).
 *
 * @module arrivalSource
 */

import {
  FOUNDING_CODE_STORAGE_KEY, FOUNDING_THIS_VISIT_SESSION_KEY, REFERRAL_AT_STORAGE_KEY, REFERRAL_CODE_STORAGE_KEY, REFERRAL_NAME_STORAGE_KEY, REFERRED_THIS_VISIT_SESSION_KEY,
  SIGNUP_FROM_STORAGE_KEY, SIGNUP_UTM_STORAGE_KEY, START_GARDEN_SESSION_KEY,
} from '../../config/tunables/shared.ts';

/** The tagged link's values kept for a sign-up. */
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign'] as const;

/** The tagged link's values, as kept and sent. */
export type SignupUtm = Partial<Record<(typeof UTM_KEYS)[number], string>>;

/** Which of the owner's own links a visitor came by: her Instagram ('ig') or her Facebook ('fb'). */
export type SignupFrom = 'ig' | 'fb';

/**
 * A ?from value as one of the owner's links, or null: "ig" or "fb", in any
 * case and with spaces around it; anything else is no link of hers.
 *
 * @param value - The value as it came (from the address, or from storage).
 * @returns The link, or null.
 */
export function signupFromOf(value: string | null | undefined): SignupFrom | null {
  const from = value?.trim().toLowerCase();
  return from === 'ig' || from === 'fb' ? from : null;
}

/** The longest value kept, as the backend keeps them (SIGNUP_SOURCE_VALUE_MAX). */
const VALUE_MAX = 100;

/** A founding code as the backend accepts it: letters, digits and hyphens. */
const CODE_PATTERN = /^[A-Za-z0-9-]{1,40}$/;

/** A share link's code, as the share page keeps it: eight letters and digits. */
const SHARE_CODE_PATTERN = /^[A-Za-z0-9]{8}$/;

/**
 * Keeps a share's credit from the address (?ref=CODE), as the share page
 * itself does (REFERRAL_CODE_STORAGE_KEY, with when, so it lapses). A
 * different share's name is forgotten with it; the same share's is kept.
 *
 * @param ref - The ?ref value, as it came.
 */
function keepShareCredit(ref: string): void {
  if (!SHARE_CODE_PATTERN.test(ref)) return;
  if (window.localStorage.getItem(REFERRAL_CODE_STORAGE_KEY) !== ref) {
    window.localStorage.removeItem(REFERRAL_NAME_STORAGE_KEY);
  }
  window.localStorage.setItem(REFERRAL_CODE_STORAGE_KEY, ref);
  window.localStorage.setItem(REFERRAL_AT_STORAGE_KEY, String(Date.now()));
  // This visit came from a share: the sign-up page may say whose garden brought them.
  try { window.sessionStorage.setItem(REFERRED_THIS_VISIT_SESSION_KEY, '1'); } catch { /* quiet where refused */ }
}

/**
 * Whether this visit came through a share's link (?ref), as against Grow's own
 * buttons with an older share's credit kept: only then does sign-up say
 * "Started from …'s garden" (owner, 2026-10-13). The credit itself is kept and
 * sent with the new account either way.
 *
 * @returns True when this visit's address carried a share's ?ref.
 */
export function referredThisVisit(): boolean {
  try { return window.sessionStorage.getItem(REFERRED_THIS_VISIT_SESSION_KEY) === '1'; } catch { return false; }
}

/**
 * Keeps what this address says about where the visitor came from: a
 * founding code (the newest one opened wins), a share's credit (?ref), that
 * the visit came to start a garden (?start or ?ref, for this tab's session),
 * which of the owner's own links brought them (?from=ig or ?from=fb; the
 * first one wins, as the tagged link's do), and the tagged link's values
 * (the first tagged visit wins, so a later visit does not take its credit).
 *
 * @param search - The address's query, `?founding=…&utm_source=…`; this page's by default.
 */
export function keepArrival(search: string = window.location.search): void {
  try {
    const params = new URLSearchParams(search);
    const code = params.get('founding')?.trim() ?? '';
    if (CODE_PATTERN.test(code)) {
      window.localStorage.setItem(FOUNDING_CODE_STORAGE_KEY, code);
      // This visit came by a founding link: the landing page may lead with the founding welcome (foundingThisVisit).
      window.sessionStorage.setItem(FOUNDING_THIS_VISIT_SESSION_KEY, '1');
    }
    // Came to start a garden: a "Start a garden" link, or a share's.
    const ref = params.get('ref')?.trim() ?? '';
    if (ref) keepShareCredit(ref);
    // ?landing=1 (the server-drawn pages' "What is Grow?") keeps the credit but shows the landing page first.
    if (params.has('start') || (SHARE_CODE_PATTERN.test(ref) && !params.has('landing'))) window.sessionStorage.setItem(START_GARDEN_SESSION_KEY, '1');
    // The owner's Instagram or Facebook link: the follower trial at sign-up.
    const from = signupFromOf(params.get('from'));
    if (from && !keptFrom()) window.localStorage.setItem(SIGNUP_FROM_STORAGE_KEY, from);
    if (window.localStorage.getItem(SIGNUP_UTM_STORAGE_KEY)) return;
    const utm: SignupUtm = {};
    for (const key of UTM_KEYS) {
      const value = params.get(key)?.trim().slice(0, VALUE_MAX);
      if (value) utm[key] = value;
    }
    if (Object.keys(utm).length > 0) window.localStorage.setItem(SIGNUP_UTM_STORAGE_KEY, JSON.stringify(utm));
  } catch { /* private mode: the link counts for nothing, and signing up still works */ }
}

/**
 * Whether this visit came by a founding link (?founding=), as against a code
 * kept from an earlier one: only then does the landing page lead with the
 * founding welcome (owner, 2026-10-14). Sign-up still sends a kept code.
 *
 * @returns True when this visit's address carried a founding code.
 */
export function foundingThisVisit(): boolean {
  try { return window.sessionStorage.getItem(FOUNDING_THIS_VISIT_SESSION_KEY) === '1'; } catch { return false; }
}

/** The founding code a link brought, if one is kept. */
export function keptFoundingCode(): string | null {
  try {
    const code = window.localStorage.getItem(FOUNDING_CODE_STORAGE_KEY);
    return code && CODE_PATTERN.test(code) ? code : null;
  } catch {
    return null;
  }
}

/** The first tagged link's values, if any were kept; empty otherwise. */
export function keptUtm(): SignupUtm {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(SIGNUP_UTM_STORAGE_KEY) ?? '{}');
    if (!parsed || typeof parsed !== 'object') return {};
    const utm: SignupUtm = {};
    for (const key of UTM_KEYS) {
      const value = (parsed as Record<string, unknown>)[key];
      if (typeof value === 'string' && value) utm[key] = value.slice(0, VALUE_MAX);
    }
    return utm;
  } catch {
    return {};
  }
}

/**
 * Which of the owner's own links this browser first arrived by, if one did:
 * 'ig' (her Instagram) or 'fb' (her Facebook). Sent with the sign-up; the
 * landing page reads it too, so a follower who comes back without the link
 * still sees their own hero and trial days.
 *
 * @returns The link, or null.
 */
export function keptFrom(): SignupFrom | null {
  try {
    return signupFromOf(window.localStorage.getItem(SIGNUP_FROM_STORAGE_KEY));
  } catch {
    return null;
  }
}

/** Whether this visit came to start a garden (?start, or a share's ?ref), so sign-in opens on signing up. */
export function startsGarden(): boolean {
  try {
    return window.sessionStorage.getItem(START_GARDEN_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Forgets that this visit came to start a garden: Grow's mark on a public page
 * leads back to the landing page even after "Start your garden" was tapped.
 */
export function forgetStartGarden(): void {
  try { window.sessionStorage.removeItem(START_GARDEN_SESSION_KEY); } catch { /* nothing to clear */ }
}

/** Forgets the founding code (sent with a sign-up, or claimed). */
export function forgetFoundingCode(): void {
  try { window.localStorage.removeItem(FOUNDING_CODE_STORAGE_KEY); } catch { /* nothing to clear */ }
}

/** Forgets everything kept about the arrival, once an account has been made with it. */
export function forgetArrival(): void {
  forgetFoundingCode();
  try { window.localStorage.removeItem(SIGNUP_UTM_STORAGE_KEY); } catch { /* nothing to clear */ }
  try { window.localStorage.removeItem(SIGNUP_FROM_STORAGE_KEY); } catch { /* nothing to clear */ }
  // Started: signing out later opens on signing in again.
  try { window.sessionStorage.removeItem(START_GARDEN_SESSION_KEY); } catch { /* nothing to clear */ }
}
